import type { Thing } from "schema-dts";
import { ContentFieldType } from "../../types/cms/contentField.type";
import { interpolate } from "../interpolate";
import type { DynamicStructure } from "../interpolate/types";
import type { ChannelData, ChannelUnit } from "../s3";
import { buildSiteBaseUrl } from "../siteUrl";
import type { Brand } from "../brands";
import { buildAutoDealers } from "./builders/autoDealer";
import { buildBlockNodes } from "./builders/blockNodes";
import { buildCmsEntries } from "./builders/cmsEntries";
import { buildOrganization } from "./builders/organization";
import { buildWebSite } from "./builders/website";
import { collectBlockSources } from "./helpers/blockSources";
import { collectEntrySources } from "./helpers/entrySources";
import { mergeById } from "./helpers/mergeById";
import { omitEmpty } from "./helpers/omitEmpty";
import type {
  SchemaBuilder,
  StructuredDataContext,
  StructuredDataEntrySource,
  StructuredDataGraph,
} from "./types";

export type {
  BlockStructuredDataResolver,
  SchemaBuilder,
  StructuredDataBlockSource,
  StructuredDataContext,
  StructuredDataEntrySource,
  StructuredDataGraph,
} from "./types";
export { collectBlockSources } from "./helpers/blockSources";
export { collectEntrySources } from "./helpers/entrySources";
export { parseCoordsFromMapLink, resolveCoordinates } from "./helpers/geo";
export {
  breadcrumbListId,
  dealerId,
  entryId,
  organizationId,
  websiteId,
} from "./helpers/ids";

/**
 * Registro de builders. Um tipo novo de schema entra aqui e em `builders/` —
 * nenhum outro ponto do fluxo precisa saber que ele existe.
 *
 * A ordem importa quando dois builders emitem o mesmo `@id`: na consolidação, a
 * primeira definição de cada propriedade é a que vale.
 */
const BUILDERS: SchemaBuilder[] = [
  buildWebSite,
  buildOrganization,
  buildAutoDealers,
  buildCmsEntries,
  buildBlockNodes,
];

interface BuildStructuredDataParams {
  channelData?: ChannelData | Record<string, any> | null;
  host?: string | null;
  dynamicPageContent?: Record<string, any> | null;
  dynamicContents?: Record<string, unknown> | null;
  dynamicContentsData?: Record<string, any> | null;
  pageData?: Record<string, any> | null;
  itemListingLink?: string | null;
  brands?: Brand[];
  isNoIndex?: boolean;
  unitUrls?: Record<string, string>;
  /** URL canônica da página, calculada pela rota (ver `canonicalUrl.ts`). */
  canonicalUrl?: string | null;
}

function isActive(unit: ChannelUnit): boolean {
  return unit.active !== false;
}

export function resolveStructuredDataScope({
  isItemPage,
  structure,
  units,
}: {
  isItemPage: boolean;
  structure: DynamicStructure;
  units: ChannelUnit[];
}): string[] {
  if (!isItemPage) return units.map((unit) => unit.id);

  const unit = interpolate({
    structure,
    infer: { type: ContentFieldType.UNIT_ID, context: "item" },
  }).unit;

  if (!unit) return [];

  return units.some((candidate) => candidate.id === unit.id) ? [unit.id] : [];
}

/** Monta o `@graph` a partir do contexto já resolvido. */
export function buildGraph(
  context: StructuredDataContext,
): StructuredDataGraph | null {
  const nodes: Thing[] = BUILDERS.flatMap((builder) => builder(context));
  const graph = omitEmpty(mergeById(nodes));

  if (!graph) return null;

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

/**
 * Dados estruturados da página, prontos para serem serializados num
 * `<script type="application/ld+json">`. Retorna `null` quando a página está
 * marcada como `noindex`, quando não há canal publicado ou quando não sobra
 * nenhum nó com conteúdo.
 */
export function buildStructuredData({
  channelData,
  host,
  dynamicPageContent,
  dynamicContents,
  dynamicContentsData,
  pageData,
  itemListingLink,
  brands,
  isNoIndex,
  unitUrls,
  canonicalUrl,
}: BuildStructuredDataParams): StructuredDataGraph | null {
  if (isNoIndex) return null;
  if (!channelData) return null;

  const baseUrl = buildSiteBaseUrl({ channelData, host });
  if (!baseUrl) return null;

  const channelPublicId = channelData.publicId;
  if (!channelPublicId) return null;

  const units = ((channelData.units as ChannelUnit[]) ?? []).filter(isActive);

  const structure: DynamicStructure = {
    item: dynamicPageContent || {},
    channel: channelData as DynamicStructure["channel"],
  };

  const fullUnitIds = resolveStructuredDataScope({
    isItemPage: Boolean(dynamicPageContent),
    structure,
    units,
  });

  return buildGraph({
    baseUrl,
    channelPublicId,
    canonicalUrl,
    siteName: channelData.name ?? null,
    organization: {
      name: channelData.organization?.name || channelData.name || null,
      logo: channelData.organization?.logo ?? null,
      socialMedia: channelData.organization?.socialMedia ?? [],
      brandName: channelData.brandName ?? null,
      parentName: null,
      parentUrl: null,
    },
    units,
    fullUnitIds,
    unitUrls,
    entrySources: collectEntrySources({
      dynamicPageContent,
      dynamicContents,
      dynamicContentsData,
      pageData,
      itemListingLink,
    }),
    blockSources: collectBlockSources({
      pageData,
      dynamicPageContent,
      dynamicContentsData,
      channelData,
      brands,
    }),
    brands: brands ?? [],
  });
}
